import { useCallback, useEffect, useState } from "react";
import { filterJobs } from "../../../../../../config/Services";
import { assertKitchenResponse } from "../../../../KDS/kitchenActions";
import { getKotKitchenDateRange } from "./kotKitchenTiming";

export const KOT_KITCHEN_POLL_MS = 60000;

export async function loadKotKitchenJobs(fetchJobs, { orgId, locId, cartId, ...dates }, isCanceled = () => false) {
  const seen = new Set();
  const matching = new Map();
  let page = 1;
  let total = Infinity;
  while (seen.size < total) {
    if (isCanceled()) throw new Error("Kitchen tracking stopped.");
    if (page > 100) throw new Error("Too many historical kitchen orders to load safely. Please retry.");
    const response = assertKitchenResponse(await fetchJobs({
      orgId, locationId: locId, ...dates,
      statuses: ["PENDING", "IN_PROGRESS", "COMPLETED", "REJECTED"],
      sortByCreatedDate: "ASCENDING", page, pageSize: 100,
    }));
    if (isCanceled()) throw new Error("Kitchen tracking stopped.");
    const data = response?.data;
    if (!Array.isArray(data?.results) || data.totalResults == null ||
      !Number.isFinite(Number(data.totalResults)) || Number(data.totalResults) < 0) {
      throw new Error("Unable to read kitchen item statuses.");
    }
    total = Number(data.totalResults);
    const before = seen.size;
    data.results.forEach((job) => {
      if (!job?.jobId) throw new Error("Kitchen order details are incomplete.");
      seen.add(String(job.jobId));
      if (String(job.metadata?.cartId ?? "") === String(cartId)) {
        if (!Array.isArray(job.tasks)) throw new Error("Kitchen order details are incomplete.");
        matching.set(String(job.jobId), job);
      }
    });
    if (seen.size < total && seen.size === before) throw new Error("Some kitchen orders could not be loaded. Please retry.");
    page += 1;
  }
  return [...matching.values()];
}

export default function useKotKitchenJobs({ open, orgId, locId, cartId, groups = [] }) {
  const dates = getKotKitchenDateRange(groups);
  const itemKey = JSON.stringify(groups.flatMap((group) => (group.items || [])
    .map((item) => [item.cartProductId, group.sequence, item.quantity, item.placedTime, item.productStatus])));
  const scope = JSON.stringify([orgId, locId, cartId, dates.createdStart, dates.createdEnd, itemKey]);
  const [snapshot, setSnapshot] = useState({ scope: "", status: "idle", jobs: [], error: "" });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const hasItems = groups.some((group) => group.items?.length);
  useEffect(() => {
    let canceled = false;
    let inFlight = false;
    if (!open || !hasItems) {
      setSnapshot({ scope: "", status: "idle", jobs: [], error: "" });
      return;
    }
    setSnapshot({ scope, status: "loading", jobs: [], error: "" });
    if (!orgId || !locId || !cartId) {
      setSnapshot({ scope, status: "error", jobs: [], error: "Cart or location details are unavailable." });
      return;
    }
    const fetch = async () => {
      if (canceled || inFlight || document.hidden) return;
      inFlight = true;
      try {
        const jobs = await loadKotKitchenJobs(filterJobs, { orgId, locId, cartId, ...dates }, () => canceled);
        if (!canceled) setSnapshot({ scope, status: "ready", jobs, error: "" });
      } catch (error) {
        if (!canceled) setSnapshot({ scope, status: "error", jobs: [], error: error?.message || "Kitchen status could not be refreshed." });
      } finally { inFlight = false; }
    };
    fetch();
    const interval = setInterval(fetch, KOT_KITCHEN_POLL_MS);
    const visible = () => { if (!document.hidden) fetch(); };
    document.addEventListener("visibilitychange", visible);
    return () => {
      canceled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [open, scope, hasItems, itemKey, revision]);
  if (!open || !hasItems) return { status: "idle", jobs: [], error: "", refresh };
  return { ...(snapshot.scope === scope ? snapshot : { status: "loading", jobs: [], error: "" }), refresh };
}
